/**
 * Asset import, mapping step: the file's columns beside QRAM's fields, the file's values beside QRAM's
 * for fields with a fixed set of values (environment, status, criticality, asset type …), and where the
 * system comes from. The choices are saved with the project, so the next import of the same export
 * (a ServiceNow CMDB dump) maps itself.
 */
export interface ImportAnalysis {
  rows: number;
  columns: Array<{ header: string; field: string | null; chosen: boolean; samples: string[]; filled: number }>;
  fields: Array<{ field: string; values: string[] | null }>;
  values: Array<{ field: string; value: string; rows: number; mapped: string | null; how: 'exact' | 'saved' | 'suggested' | 'none' }>;
  asset_types: string[];
  business_units: Array<{ id: string; name: string }>;
}

/** File header → QRAM field ('' = not imported). */
export type ColumnChoice = Record<string, string>;
/** QRAM field → the file's value → the QRAM value. */
export type ValueChoice = Record<string, Record<string, string>>;

export const fieldLabel = (f: string) => f.replace(/_name$/, '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const cell = 'px-2 py-1 border-b border-ink-100 align-top';
const select = 'text-xs border border-ink-200 rounded-sm px-1 py-0.5 max-w-[16rem]';

export function ImportMappingPanel(props: {
  analysis: ImportAnalysis;
  columns: ColumnChoice;
  onColumn: (header: string, field: string) => void;
  values: ValueChoice;
  onValue: (field: string, value: string, mapped: string) => void;
  systems: Array<{ id: string; name: string; short_code: string; bu_name: string }>;
  defaultSystem: string;
  onDefaultSystem: (v: string) => void;
  newSystemsBu: string;
  onNewSystemsBu: (v: string) => void;
}) {
  const { analysis: a, columns, values } = props;
  const used = new Map<string, string>();
  for (const [h, f] of Object.entries(columns)) if (f) used.set(f, h);
  const systemColumn = used.get('system_name');
  const allowed = new Map(a.fields.map((f) => [f.field, f.values]));
  const valueRows = a.values.map((v) => ({ ...v, choice: values[v.field]?.[v.value] ?? v.mapped ?? '' }));
  const open = valueRows.filter((v) => v.how === 'none' && !values[v.field]?.[v.value]);
  const show = valueRows.filter((v) => v.how !== 'exact');

  return (
    <div className="space-y-4" data-testid="import-mapping">
      <section>
        <div className="text-sm font-medium text-ink mb-1">1. Columns</div>
        <p className="text-xs text-ink-500 mb-2">
          Each column in the file, and the QRAM field it fills. Change any match, or choose “Not imported”. Several columns can fill one
          field (OS version, u_os_version, OS Version (new) …, as teams add custom ServiceNow columns): for each row the first of them with a value is used. {a.rows} rows.
        </p>
        <div className="max-h-72 overflow-y-auto border border-ink-100 rounded-sm">
          <table className="w-full text-xs">
            <thead className="bg-ink-50 sticky top-0"><tr><th className={`${cell} text-left`}>Your column</th><th className={`${cell} text-left`}>Example values</th><th className={`${cell} text-left`}>QRAM field</th></tr></thead>
            <tbody>
              {a.columns.map((c) => {
                const f = columns[c.header] ?? '';
                const fallbackFor = f && used.get(f) !== c.header ? used.get(f) : null;
                return (
                  <tr key={c.header} className={f ? '' : 'text-ink-400'}>
                    <td className={cell}>{c.header}</td>
                    <td className={`${cell} text-ink-500`}>{c.samples.join(' · ') || <em>empty</em>}</td>
                    <td className={cell}>
                      <select className={select} value={f} onChange={(e) => props.onColumn(c.header, e.target.value)} data-testid={`map-col-${c.header}`}>
                        <option value="">Not imported</option>
                        {a.fields.map((x) => <option key={x.field} value={x.field}>{fieldLabel(x.field)}{used.has(x.field) && used.get(x.field) !== c.header ? ` (from ${used.get(x.field)})` : ''}</option>)}
                      </select>
                      {fallbackFor && <div className="text-ink-500">Used where “{fallbackFor}” is empty.</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div className="text-sm font-medium text-ink mb-1">2. System</div>
        {systemColumn ? (
          <>
            <p className="text-xs text-ink-600">Each asset’s system comes from the column “{systemColumn}”.</p>
            <label className="block text-xs text-ink-700 mt-1">
              Rows with that column empty:{' '}
              <select className={select} value={props.defaultSystem} onChange={(e) => props.onDefaultSystem(e.target.value)} data-testid="blank-system">
                <option value="">Do not import (those rows fail)</option>
                {props.systems.map((x) => <option key={x.id} value={x.short_code || x.name}>Put them in {x.name} ({x.bu_name})</option>)}
              </select>
            </label>
          </>
        ) : (
          <label className="block text-xs text-ink-700">
            No column gives the system. Map one above (for example a department or application column), or put every row in:{' '}
            <select className={select} value={props.defaultSystem} onChange={(e) => props.onDefaultSystem(e.target.value)} data-testid="default-system">
              <option value="">Choose…</option>
              {props.systems.map((x) => <option key={x.id} value={x.short_code || x.name}>{x.name} ({x.bu_name})</option>)}
            </select>
          </label>
        )}
        {systemColumn && (
          a.business_units.length ? (
            <label className="block text-xs text-ink-700 mt-1">
              Systems the project does not have yet:{' '}
              <select className={select} value={props.newSystemsBu} onChange={(e) => props.onNewSystemsBu(e.target.value)} data-testid="new-systems-bu">
                <option value="">Do not create (those rows fail)</option>
                {a.business_units.map((b) => <option key={b.id} value={b.id}>Create them in {b.name}</option>)}
              </select>
            </label>
          ) : <p className="text-xs text-ink-500 mt-1">This project covers a single system, so new systems cannot be added here.</p>
        )}
      </section>

      {show.length > 0 && (
        <section>
          <div className="text-sm font-medium text-ink mb-1">3. Values</div>
          <p className="text-xs text-ink-500 mb-2">
            Fields with a fixed set of values. {open.length ? <strong className="text-risk-critical">{open.length} values need a choice.</strong> : 'Every value has a match.'}{' '}
            Values QRAM reads as they are are not listed. An asset type left unmatched is imported as Configuration Item (generic).
          </p>
          <div className="max-h-72 overflow-y-auto border border-ink-100 rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-ink-50 sticky top-0"><tr><th className={`${cell} text-left`}>Field</th><th className={`${cell} text-left`}>Your value</th><th className={`${cell} text-right`}>Rows</th><th className={`${cell} text-left`}>QRAM value</th></tr></thead>
              <tbody>
                {show.map((v) => (
                  <tr key={`${v.field}|${v.value}`} className={v.how === 'none' && !values[v.field]?.[v.value] ? 'bg-amber-50' : ''}>
                    <td className={cell}>{fieldLabel(v.field)}</td>
                    <td className={cell}>{v.value}</td>
                    <td className={`${cell} text-right`}>{v.rows}</td>
                    <td className={cell}>
                      <select className={select} value={v.choice} onChange={(e) => props.onValue(v.field, v.value, e.target.value)} data-testid={`map-val-${v.field}-${v.value}`}>
                        <option value="">{v.field === 'asset_type_name' ? 'Configuration Item (generic)' : 'Choose…'}</option>
                        {(allowed.get(v.field) ?? []).map((x) => <option key={x} value={x}>{x.replace(/_/g, ' ')}</option>)}
                      </select>
                      {v.how === 'suggested' && !values[v.field]?.[v.value] && <span className="ml-1 text-ink-400">suggested</span>}
                      {v.how === 'saved' && !values[v.field]?.[v.value] && <span className="ml-1 text-ink-400">saved</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
